import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import type { FC } from 'react';

interface FaqItem {
  q: string;
  a: string;
}

const homeFaqs: FaqItem[] = [
  {
    q: 'Czym jest Kakobuy Spreadsheet?',
    a: 'Kakobuy Spreadsheet to kompleksowa baza danych produktów dostępnych na platformie Kakobuy. Zawiera szczegółowe informacje o produktach, kategoriach, cenach i linkach do zakupu. Dzięki arkuszowi możesz szybko znaleźć interesujące Cię produkty bez konieczności ręcznego przeszukiwania platformy.'
  },
  {
    q: 'Czy Kakobuy działa w Polsce?',
    a: 'Tak, Kakobuy w pełni obsługuje zamówienia do Polski. Platforma oferuje międzynarodową wysyłkę, a polscy użytkownicy mogą bez problemu zamawiać produkty i korzystać ze wszystkich funkcji platformy, w tym z arkusza Kakobuy Spreadsheet.'
  },
  {
    q: 'Czy Kakobuy Spreadsheet jest bezpieczny w użyciu?',
    a: 'Kakobuy Spreadsheet jest narzędziem informacyjnym tworzonym przez społeczność. Sam arkusz jest bezpieczny – zawiera wyłącznie dane o produktach. Zawsze jednak zalecamy weryfikację produktów przed zakupem i korzystanie z oficjalnych metod płatności platformy Kakobuy.'
  },
  {
    q: 'Jak znaleźć najlepsze produkty w Kakobuy Spreadsheet?',
    a: 'Najlepsze produkty znajdziesz przeglądając kategorie w arkuszu, sortując według popularności i ocen innych użytkowników. Warto również regularnie sprawdzać aktualizacje arkusza, ponieważ nowe produkty są dodawane na bieżąco.'
  },
  {
    q: 'Jak często aktualizowany jest Kakobuy Spreadsheet?',
    a: 'Kakobuy Spreadsheet jest regularnie aktualizowany przez społeczność użytkowników. Nowe produkty i kategorie są dodawane systematycznie, aby zapewnić dostęp do najświeższych informacji o ofercie platformy Kakobuy.'
  }
];

const comparisonRows = [
  { feature: 'Kategorie produktów', spreadsheet: 'Tak, rozbudowane', traditional: 'Ograniczone' },
  { feature: 'Filtry zaawansowane', spreadsheet: 'Tak, szczegółowe', traditional: 'Podstawowe' },
  { feature: 'Oszczędność czasu', spreadsheet: 'Bardzo wysoka', traditional: 'Niska' },
  { feature: 'Organizacja danych', spreadsheet: 'Profesjonalna', traditional: 'Chaotyczna' },
  { feature: 'Aktualizacje społeczności', spreadsheet: 'Regularne', traditional: 'Brak' },
];

const safetyTips = [
  { icon: 'ri-chat-check-line', text: 'Zawsze sprawdzaj opinie innych użytkowników przed zakupem' },
  { icon: 'ri-link', text: 'Korzystaj wyłącznie z oficjalnych linków i arkuszy' },
  { icon: 'ri-image-line', text: 'Weryfikuj jakość produktów przez zdjęcia QC z magazynu' },
  { icon: 'ri-bank-card-line', text: 'Używaj bezpiecznych metod płatności oferowanych przez platformę' },
  { icon: 'ri-truck-line', text: 'Sprawdzaj czas wysyłki i koszty przed złożeniem zamówienia' },
];

const steps = [
  { step: '01', title: 'Wybierz kategorię', desc: 'Przejrzyj dostępne kategorie produktów i wybierz tę, która Cię interesuje.' },
  { step: '02', title: 'Przeglądaj produkty', desc: 'Sortuj według popularności, ceny lub ocen innych użytkowników.' },
  { step: '03', title: 'Kupuj bezpiecznie', desc: 'Skorzystaj z linków w arkuszu i oficjalnych metod płatności Kakobuy.' },
];

const ContentSection: FC = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    const scriptId = 'faq-schema-jsonld';
    if (document.getElementById(scriptId)) return;

    const script = document.createElement('script');
    script.id = scriptId;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: homeFaqs.map((faq) => ({
        '@type': 'Question',
        name: faq.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.a,
        },
      })),
    });
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById(scriptId);
      if (existing) existing.remove();
    };
  }, []);

  return (
    <>
      <section id="about" className="py-16 md:py-24 bg-background-100">
        <div className="section-container max-w-4xl">
          <div className="text-center mb-12 animate-fade-in-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-100 text-primary-800 text-xs font-medium mb-4">
              <i className="ri-information-line text-sm"></i>
              <span>O Kakobuy Spreadsheet</span>
            </div>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
              Dlaczego Kakobuy Spreadsheet zmienia sposób zakupów online?
            </h2>
          </div>

          <div className="space-y-6 animate-fade-in-up animation-delay-100">
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
              W dzisiejszych czasach zakupy na platformach takich jak Kakobuy stają się coraz popularniejsze wśród polskich konsumentów. Jednak przeszukiwanie tysięcy produktów ręcznie może być czasochłonne i frustrujące. Właśnie dlatego powstał <strong className="text-foreground-900">Kakobuy Spreadsheet</strong> – narzędzie stworzone przez społeczność dla społeczności, które rewolucjonizuje sposób wyszukiwania produktów.
            </p>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
              Arkusz Kakobuy Spreadsheet to coś więcej niż zwykła lista produktów. To starannie posegregowana baza danych, która zawiera informacje o kategoriach, cenach, popularności i linkach do zakupu. Dzięki aktywnym użytkownikom z całego świata, a szczególnie z Polski, arkusz jest nieustannie rozbudowywany i aktualizowany. Polscy użytkownicy znajdą w nim produkty sprawdzone przez innych kupujących z naszego kraju, wraz z praktycznymi wskazówkami dotyczącymi wysyłki do Polski.
            </p>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
              Korzystanie z Kakobuy Spreadsheet to najszybszy sposób na odkrywanie <strong className="text-foreground-900">najlepszych produktów Kakobuy</strong>. Niezależnie od tego, czy interesują Cię modne sneakersy, wysokiej jakości odzież streetwear, praktyczne akcesoria czy designerskie dodatki – arkusz zawiera setki sprawdzonych pozycji z każdej kategorii. Co więcej, społeczność regularnie dodaje nowe odkrycia (Kakobuy finds), dzięki czemu zawsze masz dostęp do najświeższych propozycji.
            </p>

            <div className="bg-background-50 rounded-xl p-6 md:p-8 border border-background-200/70 mt-8">
              <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-900 mb-4">
                Jak zacząć korzystać z Kakobuy Spreadsheet?
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
                {steps.map((item, i) => (
                  <div key={i} className="text-center p-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-sm font-bold mx-auto mb-3">
                      {item.step}
                    </div>
                    <h4 className="font-semibold text-sm text-foreground-800 mb-1.5">{item.title}</h4>
                    <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
              <div className="text-center mt-6">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="btn-primary inline-flex px-6 py-3 text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 rounded-md transition-all duration-200"
                >
                  Otwórz Kakobuy Spreadsheet
                  <i className="ri-arrow-right-up-line ml-2"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="comparison" className="py-16 md:py-24 bg-background-50">
        <div className="section-container">
          <div className="text-center mb-12 animate-fade-in-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-800 text-xs font-medium mb-4">
              <i className="ri-scales-line text-sm"></i>
              <span>Porównanie</span>
            </div>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
              Kakobuy Spreadsheet vs Tradycyjne wyszukiwanie
            </h2>
          </div>

          <div className="max-w-3xl mx-auto animate-fade-in-up animation-delay-100">
            <div className="overflow-hidden rounded-xl border border-background-200/70">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-background-100">
                    <th className="text-left px-5 py-4 font-semibold text-foreground-800">Funkcja</th>
                    <th className="text-left px-5 py-4 font-semibold text-primary-600">
                      <span className="flex items-center gap-1.5">
                        <i className="ri-check-double-line"></i> Spreadsheet
                      </span>
                    </th>
                    <th className="text-left px-5 py-4 font-semibold text-foreground-500">Tradycyjne wyszukiwanie</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonRows.map((row, i) => (
                    <tr key={i} className={`border-t border-background-100 ${i % 2 === 0 ? 'bg-transparent' : 'bg-background-50/50'}`}>
                      <td className="px-5 py-3.5 text-foreground-700">{row.feature}</td>
                      <td className="px-5 py-3.5">
                        <span className="inline-flex items-center gap-1.5 text-primary-600 font-medium">
                          <i className="ri-check-line text-sm"></i>
                          {row.spreadsheet}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-foreground-500">{row.traditional}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      <section id="safety" className="py-16 md:py-24 bg-background-100">
        <div className="section-container">
          <div className="text-center mb-12 animate-fade-in-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-100 text-primary-800 text-xs font-medium mb-4">
              <i className="ri-shield-check-line text-sm"></i>
              <span>Bezpieczeństwo</span>
            </div>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
              Czy Kakobuy Spreadsheet jest bezpieczny?
            </h2>
            <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto leading-relaxed">
              Poznaj zasady bezpiecznego korzystania z platformy Kakobuy
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 max-w-4xl mx-auto">
            {safetyTips.map((tip, i) => (
              <div
                key={i}
                className="flex items-start gap-3 p-5 rounded-lg bg-background-50 border border-background-200/70 animate-fade-in-up"
                style={{ animationDelay: `${i * 0.1}s` }}
              >
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                  <i className={`${tip.icon} text-sm`}></i>
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed">{tip.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="article-promo" className="py-16 md:py-20 bg-background-50">
        <div className="section-container">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary-100 via-background-50 to-accent-100 border border-background-200/70 p-8 md:p-12 animate-fade-in-up">
            <div className="relative z-10 flex flex-col md:flex-row items-center gap-8">
              <div className="flex-1 text-center md:text-left">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                  Kakobuy Spreadsheet Polska – Kompletny Przewodnik 2026
                </h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6 max-w-lg">
                  Dowiedz się wszystkiego o Kakobuy Spreadsheet – narzędziu, które rewolucjonizuje sposób wyszukiwania produktów na platformie Kakobuy. Poznaj kategorie, porady i bezpieczne metody zakupowe dla polskich użytkowników.
                </p>
                <div className="flex flex-wrap items-center gap-3 justify-center md:justify-start">
                  <Link
                    to="/artykul/kakobuy-spreadsheet-polska-kompletny-przewodnik"
                    className="btn-primary px-6 py-3 text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 rounded-md transition-all duration-200"
                  >
                    Czytaj artykuł
                    <i className="ri-arrow-right-line ml-2"></i>
                  </Link>
                  <span className="text-xs text-foreground-400 flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    8 min czytania
                  </span>
                  <span className="text-xs text-foreground-400 flex items-center gap-1">
                    <i className="ri-price-tag-3-line"></i>
                    Poradnik
                  </span>
                </div>
              </div>
              <div className="flex-shrink-0">
                <div className="w-24 h-24 md:w-28 md:h-28 rounded-2xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Clean%20minimalist%20e-commerce%20guidebook%20and%20laptop%20concept%20on%20warm%20cream%20background%2C%20soft%20natural%20window%20light%2C%20modern%20knowledge%20sharing%20aesthetic%2C%20editorial%20photography%2C%20warm%20neutral%20tones%2C%20simple%20elegant%20composition%20with%20subtle%20shadows%2C%20professional%20consulting%20theme%2C%20organized%20workspace%20elements&width=300&height=300&seq=article-promo-img&orientation=squarish"
                    alt="Kakobuy Spreadsheet Przewodnik"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="faq" className="py-16 md:py-24 bg-background-100">
        <div className="section-container">
          <div className="text-center mb-12 animate-fade-in-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-100 text-accent-800 text-xs font-medium mb-4">
              <i className="ri-question-answer-line text-sm"></i>
              <span>FAQ</span>
            </div>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
              Często zadawane pytania
            </h2>
            <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto leading-relaxed">
              Odpowiedzi na najczęściej zadawane pytania o Kakobuy Spreadsheet
            </p>
          </div>

          <div className="max-w-3xl mx-auto space-y-3 animate-fade-in-up animation-delay-100">
            {homeFaqs.map((faq, i) => (
              <div
                key={i}
                className="rounded-lg border border-background-200/70 bg-background-50 overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer hover:bg-background-50/80 transition-colors"
                >
                  <span className="text-sm font-medium text-foreground-800 pr-4">{faq.q}</span>
                  <i
                    className={`ri-arrow-down-s-line text-foreground-400 transition-transform duration-200 flex-shrink-0 ${
                      openFaq === i ? 'rotate-180' : ''
                    }`}
                  ></i>
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-4 animate-scale-in">
                    <p className="text-sm text-foreground-500 leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default ContentSection;